(() => {
  'use strict';

  const selector = '.ltp-video-feature';
  const records = new Map();
  const mobile = window.matchMedia('(max-width: 749px)');

  const selectedVideo = (record) => record.element.querySelector(
    mobile.matches ? '.ltp-video-feature__video--mobile' : '.ltp-video-feature__video--desktop'
  ) || record.element.querySelector('video');

  const canPlay = (record, video) => record.element.isConnected && record.visible &&
    !document.hidden && selectedVideo(record) === video && !document.querySelector('modal-dialog[open]');

  function stop(record, video) {
    const state = record.states.get(video);
    if (!state) return;
    state.generation += 1;
    state.pending = false;
    video.pause();
    video.dataset.ltpPlayback = state.loaded ? 'paused' : 'dormant';
  }

  function play(record, video) {
    const state = record.states.get(video);
    if (!state || !state.loaded || !canPlay(record, video) || state.pending || video.error || video.readyState < 2) return;
    if (!video.paused) return;
    const generation = ++state.generation;
    state.pending = true;
    video.muted = true;
    video.playsInline = true;
    video.dataset.ltpPlayback = 'starting';
    let attempt;
    try {
      attempt = video.play();
    } catch (error) {
      state.pending = false;
      video.dataset.ltpPlayback = 'waiting';
      video.dataset.ltpPlayError = error.name;
      return;
    }
    Promise.resolve(attempt).then(() => {
      if (generation !== state.generation) return;
      state.pending = false;
      if (canPlay(record, video)) {
        video.dataset.ltpPlayback = 'playing';
        delete video.dataset.ltpPlayError;
      } else {
        stop(record, video);
      }
    }).catch((error) => {
      if (generation !== state.generation) return;
      state.pending = false;
      video.dataset.ltpPlayback = 'waiting';
      video.dataset.ltpPlayError = error.name;
    });
  }

  function prepare(record, video, recover = false) {
    const state = record.states.get(video);
    if (!state || document.hidden || (!record.near && !record.visible)) return;
    if (!state.loaded) {
      const source = video.querySelector('source[data-src]');
      if (!source) return;
      state.loaded = true;
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.src = source.dataset.src;
      video.dataset.ltpPlayback = 'preparing';
      video.load();
    } else if (recover && (video.error || video.networkState === 0)) {
      state.generation += 1;
      state.pending = false;
      video.dataset.ltpPlayback = 'preparing';
      video.load();
    }
    play(record, video);
  }

  function sync(record, recover = false) {
    const selected = selectedVideo(record);
    record.states.forEach((state, video) => {
      if (video !== selected || !canPlay(record, video)) stop(record, video);
    });
    if (selected) prepare(record, selected, recover);
  }

  const prepareObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const record = records.get(entry.target);
      if (!record) return;
      record.near = entry.isIntersecting;
      sync(record, entry.isIntersecting);
    });
  }, { rootMargin: '600px 0px', threshold: 0 }) : null;

  const playbackObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const record = records.get(entry.target);
      if (!record) return;
      record.visible = entry.isIntersecting;
      sync(record, record.visible);
    });
  }, { threshold: 0 }) : null;

  function register(root = document) {
    const elements = [...root.querySelectorAll(selector)];
    if (root.matches?.(selector)) elements.unshift(root);
    elements.forEach((element) => {
      if (records.has(element)) return;
      const record = { element, near: false, visible: false, states: new Map(), cleanup: [] };
      records.set(element, record);
      element.querySelectorAll('video').forEach((video) => {
        record.states.set(video, { loaded: false, pending: false, generation: 0 });
        video.muted = true;
        video.playsInline = true;
        video.dataset.ltpPlayback = 'dormant';
        const ready = () => play(record, video);
        const playing = () => {
          if (!canPlay(record, video)) { stop(record, video); return; }
          video.dataset.ltpPlayback = 'playing';
          delete video.dataset.ltpPlayError;
        };
        const paused = () => {
          if (record.states.get(video).loaded) video.dataset.ltpPlayback = 'paused';
        };
        const failed = () => {
          const state = record.states.get(video);
          state.generation += 1;
          state.pending = false;
          video.dataset.ltpPlayback = 'error';
        };
        [['loadeddata', ready], ['canplay', ready], ['playing', playing], ['pause', paused], ['error', failed]]
          .forEach(([event, handler]) => {
            video.addEventListener(event, handler);
            record.cleanup.push(() => video.removeEventListener(event, handler));
          });
      });
      if (prepareObserver && playbackObserver) {
        prepareObserver.observe(element);
        playbackObserver.observe(element);
      } else {
        record.near = record.visible = true;
        sync(record);
      }
    });
  }

  const syncAll = () => records.forEach((record) => sync(record, true));
  document.addEventListener('visibilitychange', syncAll);
  window.addEventListener('pageshow', syncAll);
  window.addEventListener('online', syncAll);
  mobile.addEventListener('change', syncAll);
  document.addEventListener('modalClosed', () => queueMicrotask(syncAll));
  document.addEventListener('shopify:section:load', (event) => register(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    records.forEach((record, element) => {
      if (!event.target.contains(element)) return;
      prepareObserver?.unobserve(element);
      playbackObserver?.unobserve(element);
      record.states.forEach((state, video) => stop(record, video));
      record.cleanup.forEach((cleanup) => cleanup());
      records.delete(element);
    });
  });
  register();
})();
